import { Button } from "@jhool-io/fe-components";
import { Accept, FileRejection, useDropzone } from "react-dropzone";
import { cn } from "../../utils/helpers";
import FolderOpenIcon from "../../components/Icons/FolderOpen";
import TimesIcon from "../../icons/Times";

const DEFAULT_ACCEPTED_FILE_TYPES: Accept = {
    "application/pdf": [".pdf"],
    "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
};

export function FilePicker({
    children,
    onDrop,
    disabled,
    accept = DEFAULT_ACCEPTED_FILE_TYPES,
    maxSize,
    onDropRejected,
}: {
    children: React.ReactNode;
    onDrop: (acceptedFiles: File[]) => void;
    disabled?: boolean;
    accept?: Accept;
    maxSize?: number;
    onDropRejected?: (fileRejections: FileRejection[]) => void;
}) {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 0,
        disabled,
        useFsAccessApi: false,
        accept,
        maxSize,
        onDropRejected,
    });
    return (
        <div
            className={cn(
                "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                {
                    "border-[2px] border-dashed border-stroke": isDragActive,
                }
            )}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {children}
        </div>
    );
}

export function FilePickerIcon({ icon }: { icon?: React.ReactNode }) {
    return (
        <div className="p-16 bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
            {icon ?? <FolderOpenIcon />}
        </div>
    );
}

export function FilePickerMeta({
    description,
    uploadConditionText,
}: {
    description: string;
    uploadConditionText?: string;
}) {
    return (
        <div>
            <div>
                <span className="font-semibold text-textLink cursor-default">
                    {description}{" "}
                </span>
                or drag your file here
            </div>
            <p className="text-caption font-normal">
                {uploadConditionText ||
                    "Maximum file size of 10MB: .png, .jpg, .jpeg"}
            </p>
        </div>
    );
}

export function FilePickerSelectedFiles({
    selectedFiles,
    handleRemoveSelectedFile,
    wrapperClassName,
}: {
    selectedFiles: File[] | null;
    handleRemoveSelectedFile: (filename: string) => void;
    wrapperClassName?: string;
}) {
    if (!selectedFiles || (selectedFiles && selectedFiles.length === 0))
        return null;

    return (
        <div
            className={cn(
                "flex flex-col gap-y-[--spacing-items]",
                wrapperClassName
            )}
        >
            {selectedFiles &&
                selectedFiles.map((file, i) => (
                    <div
                        key={`${file.name} - ${i + 1}`}
                        className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] rounded-md px-[--padding-card-x] py-[--padding-card-y]"
                    >
                        <div className="text-caption text-xs text-textSec">
                            {file.name}
                        </div>
                        <Button
                            variant="can-hover"
                            aria-label="delete icon"
                            size="auto"
                            onClick={() => handleRemoveSelectedFile(file.name)}
                        >
                            <TimesIcon />
                        </Button>
                    </div>
                ))}
        </div>
    );
}
